import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { Card, Metric, SectionLabel, Pill } from "@/components/common";
import { Shield, Plug, Mail, Users, Download, Search } from "lucide-react";

function EmailConfig() {
  const { data, refresh } = usePolling(async () => (await api.get("/admin/email")).data, 0, []);
  const [sender, setSender] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/email", { sender: sender || null, api_key: apiKey || null });
      toast.success("Email settings saved."); setApiKey(""); refresh();
    } catch { toast.error("Could not save email settings."); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!to) return toast.error("Enter a recipient email.");
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/email/test", { to });
      if (r.ok) toast.success(`Test email sent from ${r.sender} to ${to}.`);
      else toast.error("Send failed — check the from-address is a verified domain (or use your Resend-verified inbox).");
    } catch { toast.error("Send failed."); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5" data-testid="email-config">
      <div className="flex items-center gap-2 mb-3"><Mail size={16} className="text-aurum" /><SectionLabel>Email Alerts — Resend</SectionLabel></div>
      <div className="flex items-center gap-2 mb-3 text-sm">
        <span className="text-[var(--text-sec)]">Status:</span>
        <Pill color={data?.configured ? "#21C58E" : "#FF4D67"}>{data?.configured ? "CONFIGURED" : "NOT CONFIGURED"}</Pill>
        <span className="text-xs text-[var(--text-muted)]">from: {data?.sender}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div><label className="text-xs text-[var(--text-sec)]">From-address (verified domain)</label>
          <input value={sender} onChange={(e) => setSender(e.target.value)} placeholder={data?.sender || "alerts@yourdomain.com"} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" data-testid="email-sender" /></div>
        <div><label className="text-xs text-[var(--text-sec)]">Resend API key (optional — leave blank to keep)</label>
          <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="re_…" className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" data-testid="email-apikey" /></div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <button onClick={save} disabled={busy} className="btn-gold rounded-lg px-4 py-2 text-sm font-semibold hover:brightness-110 disabled:opacity-50" data-testid="email-save">Save</button>
        <div className="flex-1 min-w-[200px]"><label className="text-xs text-[var(--text-sec)]">Send test email to</label>
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" data-testid="email-test-to" /></div>
        <button onClick={sendTest} disabled={busy} className="rounded-lg px-4 py-2 text-sm border border-[var(--border2)] hover:border-aurum hover:text-aurum" data-testid="email-test-send">Send test</button>
      </div>
      <p className="mt-3 text-[11px] text-[var(--text-muted)]">With Resend's test sender <code>onboarding@resend.dev</code>, delivery is limited to your Resend-verified email. Verify a domain at resend.com/domains and set the from-address here to reach any user.</p>
    </Card>
  );
}

function Mt5Config() {
  const { data, refresh } = usePolling(async () => (await api.get("/market/provider")).data, 0, []);
  const [url, setUrl] = useState("");
  const [symbol, setSymbol] = useState("XAUUSD");
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const bridge = data?.bridge; const health = data?.health;

  const save = async (clear) => {
    setSaving(true);
    try {
      const { data: r } = await api.post("/admin/mt5", { url: clear ? null : url, symbol, token });
      toast.success(clear ? "Using default cloud feed (Twelve Data)." : `Bridge set — status ${r.health.status}.`);
      refresh();
    } catch { toast.error("Could not update provider."); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5" data-testid="mt5-config">
      <div className="flex items-center gap-2 mb-3"><Plug size={16} className="text-aurum" /><SectionLabel>Live Market Data</SectionLabel></div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm text-[var(--text-sec)]">Current source:</span>
        <Pill color={health?.status === "LIVE" ? "#21C58E" : health?.status === "STALE" ? "#F5B942" : health?.status === "DEMO" ? "#F5B942" : "#FF4D67"}>{health?.status || "…"}</Pill>
        <span className="text-xs text-[var(--text-muted)]">{health?.message}</span>
      </div>
      <p className="text-[12px] text-[var(--text-sec)] mb-3">Aurum streams live XAU/USD from a built-in cloud feed by default — nothing to install or run. Optionally, power users can override it with their own MetaTrader 5 broker feed below.</p>
      <SectionLabel>Optional: connect your own MetaTrader 5 broker</SectionLabel>
      <div className="grid md:grid-cols-3 gap-3 mt-2">
        <div className="md:col-span-2"><label className="text-xs text-[var(--text-sec)]">Bridge URL (your Windows relay)</label>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={bridge?.url || "https://your-vps:8777"} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" data-testid="mt5-url" /></div>
        <div><label className="text-xs text-[var(--text-sec)]">Symbol</label>
          <input value={symbol} onChange={(e) => setSymbol(e.target.value)} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" /></div>
        <div className="md:col-span-3"><label className="text-xs text-[var(--text-sec)]">Bridge token (must match AURUM_BRIDGE_TOKEN on your Windows relay)</label>
          <input value={token} onChange={(e) => setToken(e.target.value)} type="password" placeholder="the secret you set in $env:AURUM_BRIDGE_TOKEN" autoComplete="off" className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" data-testid="mt5-token" /></div>
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={() => save(false)} disabled={saving || !url} className="btn-gold rounded-lg px-4 py-2 text-sm font-semibold hover:brightness-110 disabled:opacity-50" data-testid="mt5-connect">Connect MT5 bridge</button>
        <button onClick={() => save(true)} disabled={saving} className="rounded-lg px-4 py-2 text-sm border border-[var(--border2)] hover:border-aurum" data-testid="mt5-use-cloud">Use default cloud feed</button>
      </div>
      <p className="mt-3 text-[11px] text-[var(--text-muted)]">The cloud feed (Twelve Data) requires zero setup and works for every user 24/7. The MT5 bridge is optional — run <code>mt5_bridge/aurum_mt5_bridge.py</code> on a Windows machine with MetaTrader 5 logged into your broker to use your broker's exact prices instead. Data auto-marks LIVE / STALE / UNAVAILABLE.</p>
    </Card>
  );
}

function WaitlistAdmin() {
  const { data } = usePolling(async () => (await api.get("/admin/waitlist")).data, 0, []);
  const [q, setQ] = useState("");
  const rows = data?.waitlist || [];
  const filtered = rows.filter((r) => r.email.toLowerCase().includes(q.toLowerCase()));

  const exportCsv = () => {
    const header = "email,interest,created_at\n";
    const body = rows.map((r) => `${r.email},${r.interest || ""},${r.created_at || ""}`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `aurum-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} entries.`);
  };

  return (
    <Card className="p-5" data-testid="waitlist-admin">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2"><Users size={16} className="text-aurum" /><SectionLabel>Early-Access Waitlist</SectionLabel><Pill color="#D6AE57">{data?.count ?? 0}</Pill></div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…" data-testid="waitlist-search"
              className="rounded-lg bg-[var(--surface)] border border-[var(--border2)] pl-8 pr-3 py-1.5 text-sm outline-none focus:border-aurum" />
          </div>
          <button onClick={exportCsv} disabled={!rows.length} data-testid="waitlist-export"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--border2)] px-3 py-1.5 text-sm hover:border-aurum hover:text-aurum disabled:opacity-40">
            <Download size={14} /> CSV
          </button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[var(--text-muted)] sticky top-0 bg-[var(--card2)]">
            <th className="py-2">Email</th><th>Interest</th><th>Joined</th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-[var(--text-muted)]">{rows.length ? "No matches." : "No signups yet."}</td></tr>}
            {filtered.map((r) => (
              <tr key={r.email} className="border-t border-[var(--border-subtle)]">
                <td className="py-2">{r.email}</td>
                <td className="text-[var(--text-sec)]">{r.interest || "—"}</td>
                <td className="text-[var(--text-muted)]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Admin() {
  const { data: stats } = usePolling(async () => (await api.get("/admin/stats")).data, 0, []);
  const { data: usersData } = usePolling(async () => (await api.get("/admin/users")).data, 0, []);
  const providers = stats?.providers || {};

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center gap-3"><Shield className="text-aurum" /><h1 className="text-2xl font-bold tracking-tight">Admin Panel</h1></div>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4"><Metric label="Total Users" value={stats.total_users} /></Card>
          <Card className="p-4"><Metric label="Paper Trades" value={stats.total_paper_trades} /></Card>
          {Object.entries(stats.plan_distribution || {}).map(([k, v]) => (
            <Card key={k} className="p-4"><Metric label={`${k} plan`} value={v} /></Card>
          ))}
        </div>
      )}
      <Mt5Config />
      <EmailConfig />
      <WaitlistAdmin />
      <Card className="p-5">
        <SectionLabel>Provider Health & Configuration</SectionLabel>
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          <Prov name="Market Data" status={providers.market_data?.status} msg={providers.market_data?.message} />
          <Prov name="AI" status={providers.ai?.configured ? "CONFIGURED" : "NOT CONFIGURED"} msg={`${providers.ai?.provider} · ${providers.ai?.model}`} />
          <Prov name="Economic Calendar" status={providers.economic_calendar?.status} msg="Demo calendar generator" />
          <Prov name="Notifications" status="IN-APP" msg={providers.notifications?.email_sms_push} />
          <Prov name="Payments" status={providers.payments?.status} msg="No billing during launch" />
        </div>
      </Card>
      <Card className="p-5">
        <SectionLabel>Users</SectionLabel>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[var(--text-muted)]">
              <th className="py-2">Name</th><th>Email</th><th>Role</th><th>Plan</th><th>Onboarded</th></tr></thead>
            <tbody>
              {(usersData?.users || []).map((u) => (
                <tr key={u.id} className="border-t border-[var(--border-subtle)]">
                  <td className="py-2">{u.name}</td><td className="text-[var(--text-sec)]">{u.email}</td>
                  <td><Pill color={u.role === "admin" ? "#D6AE57" : "#98A2B3"}>{u.role}</Pill></td>
                  <td>{u.plan}</td><td>{u.onboarded ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Prov({ name, status, msg }) {
  const bad = /NOT CONFIGURED/i.test(status || "");
  const color = bad ? "#F5B942" : status === "DEMO" ? "#F5B942" : "#21C58E";
  return (
    <div className="rounded-lg border border-[var(--border2)] p-3 flex items-center justify-between">
      <div><div className="text-sm font-medium">{name}</div><div className="text-[11px] text-[var(--text-muted)]">{msg}</div></div>
      <Pill color={color}>{status}</Pill>
    </div>
  );
}
